import { Component, Element, Host, Prop, Watch, h } from '@stencil/core';
import { debounce } from './debounce.function';
import { isFocusable, isHidden } from './focusable.function';
import { queryShadowRoot } from './shadow.function';

/**
 * @slot  - Slot to add components to focus trap.
 */
@Component({
  tag: 'sy-focus-trap',
  shadow: true,
})
export class FocusTrap {
  // The backup element is only used if there are no other focusable children
  private backup: HTMLElement | null | undefined;
  private start: HTMLElement | null | undefined;
  private end: HTMLElement | null | undefined;

  private focusLastElementBounded: () => void;
  private focusFirstElementBounded: () => void;
  private onFocusInBounded: () => void;
  private onFocusOutBounded: () => void;

  private focused = false;

  // The debounce id is used to distinguish this focus trap from others when debouncing
  private debounceId = Math.random().toString();

  @Element() el: HTMLSyFocusTrapElement;

  /**
   * If true, the focus won't trap the focus.
   */
  @Prop({ reflect: true }) disabled = false;

  /**
   * Sets the tabIndex value of the trap divs
   */
  @Watch('disabled')
  private disabledWatcher() {
    this.start?.setAttribute('tabindex', !this.focused || this.disabled ? `-1` : `0`);
    this.end?.setAttribute('tabindex', !this.focused || this.disabled ? `-1` : `0`);
  }

  componentDidLoad() {
    this.addHandler();
  }

  disconnectedCallback() {
    if (this.el.isConnected) return;

    this.removeHandler();
  }

  /**
   * Focuses the first focusable element in the focus trap.
   */
  private focusFirstElement() {
    this.trapFocus();
  }

  /**
   * Focuses the last focusable element in the focus trap.
   */
  private focusLastElement() {
    this.trapFocus(true);
  }

  /**
   * Focuses on either the last or first focusable element.
   * @param {boolean} trapToEnd
   */
  private trapFocus(trapToEnd?: boolean) {
    if (this.disabled) return;

    const focusableElement = this.getNextFocusableElement(trapToEnd);
    if (focusableElement) {
      focusableElement.focus();
      this.backup?.setAttribute('tabindex', '-1');
    } else {
      // If there are no focusable children we need to focus on the backup
      // to trap the focus. This is a useful behavior if the focus trap is
      // for example used in a dialog and we don't want the user to tab
      // outside the dialog even though there are no focusable children
      // in the dialog.
      this.backup?.setAttribute('tabindex', '0');
      this.backup?.focus();
    }
  }

  /**
   * Returns a list of the focusable children found within the element.
   */
  private getNextFocusableElement(reverse?: boolean): HTMLElement | null {
    return queryShadowRoot(this.el, isHidden, isFocusable, reverse);
  }

  /**
   * When the element gains focus this function is called.
   */
  private onFocusIn() {
    this.updateFocused(true);
  }

  /**
   * When the element looses its focus this function is called.
   */
  private onFocusOut() {
    this.updateFocused(false);
  }

  /**
   * Updates the focused property and updates the view.
   * The update is debounced because the focusin and focusout out
   * might fire multiple times in a row. We only want to render
   * the element once, therefore waiting until the focus is "stable".
   * @param value
   */
  private updateFocused(value: boolean) {
    debounce(
      () => {
        if (this.focused !== value) {
          this.focused = value;
          this.disabledWatcher();
        }
      },
      0,
      this.debounceId,
    );
  }

  /**
   * Adds handlers to deal with focus events
   */
  private addHandler() {
    const root = this.el.shadowRoot;

    this.backup = root?.querySelector('#backup');
    this.start = root?.querySelector('#start');
    this.end = root?.querySelector('#end');

    this.focusLastElementBounded = this.focusLastElement.bind(this);
    this.focusFirstElementBounded = this.focusFirstElement.bind(this);
    this.onFocusInBounded = this.onFocusIn.bind(this);
    this.onFocusOutBounded = this.onFocusOut.bind(this);

    this.start?.addEventListener('focus', this.focusLastElementBounded);
    this.end?.addEventListener('focus', this.focusFirstElementBounded);

    // Focus out is called every time the user tabs around inside the element
    this.el.addEventListener('focusin', this.onFocusInBounded);
    this.el.addEventListener('focusout', this.onFocusOutBounded);

    this.disabledWatcher();
  }

  /**
   * Removes handlers to deal with focus events
   */
  private removeHandler() {
    this.start?.removeEventListener('focus', this.focusLastElementBounded);
    this.end?.removeEventListener('focus', this.focusFirstElementBounded);
    this.el.removeEventListener('focusin', this.onFocusInBounded);
    this.el.removeEventListener('focusout', this.onFocusOutBounded);
  }

  render() {
    return (
      <Host>
        <div id="start"></div>
        <div id="backup"></div>
        <slot></slot>
        <div id="end"></div>
      </Host>
    );
  }
}
